/**
 * Cómo se lee el trabajo de una impresora.
 *
 * Aparte de la pantalla porque el agrupado tiene bordes —dos OTs en curso, una
 * sin fecha de arranque, un estado que el enum todavía no tenía— y una pantalla
 * no se puede poner en un test sin un browser.
 */

/** Lo que el agrupado necesita de una OT. El resto de la fila no le importa. */
export interface OrdenDeImpresora {
  id: string;
  numero: string;
  estado: string;
  producto: string;
  cliente: string;
  fechaInicioImpresion: string | null;
}

/**
 * La impresora está tomada: es lo que se está haciendo ahora.
 *
 * PAUSADA cuenta —la máquina sigue con el trabajo montado— y APROBACION_
 * PENDIENTE también: la prueba ya se imprimió y se espera al cliente.
 */
const EN_CURSO = new Set(["REALIZANDO_PRUEBA", "APROBACION_PENDIENTE", "EN_IMPRESION", "PAUSADA"]);

/** Ya no hay nada que hacer en la máquina. */
const TERMINADAS = new Set(["FINALIZADA", "ENTREGADA", "CERRADA", "RECHAZADA", "CANCELADA"]);

/**
 * Reparte las OTs de una impresora en los tres bloques de
 * `PrinterDetailPage.tsx`: la que se está haciendo, las que esperan turno y las
 * que ya se cerraron.
 *
 * **Una sola en curso**, porque una máquina no imprime dos cosas a la vez. Si
 * hay más de una en esos estados —pasa cuando alguien pausa algo y arranca lo
 * siguiente sin reasignar— gana la que **arrancó más tarde**, y las demás bajan
 * a pendientes en vez de desaparecer. Es el desempate de allá.
 *
 * **CANCELADA va a terminadas, y ahí `imprenta` difiere** (ADR 0066): allá no
 * está en ninguno de los tres conjuntos y cae en el `else` que manda a
 * pendientes, así que la ficha ofrece trabajo que nadie va a hacer.
 *
 * Un estado que no esté en ninguna lista igual aparece, en pendientes: si el
 * enum crece y nadie toca esto, la OT tiene que seguir viéndose. Desaparecer en
 * silencio es peor que estar mal puesta.
 */
export function agruparOrdenes(ordenes: readonly OrdenDeImpresora[]): {
  enCurso: OrdenDeImpresora | null;
  pendientes: OrdenDeImpresora[];
  terminadas: OrdenDeImpresora[];
} {
  let enCurso: OrdenDeImpresora | null = null;
  const pendientes: OrdenDeImpresora[] = [];
  const terminadas: OrdenDeImpresora[] = [];

  for (const orden of ordenes) {
    if (EN_CURSO.has(orden.estado)) {
      if (!enCurso) {
        enCurso = orden;
      } else if (arrancoDespues(orden, enCurso)) {
        pendientes.push(enCurso);
        enCurso = orden;
      } else {
        pendientes.push(orden);
      }
    } else if (TERMINADAS.has(orden.estado)) {
      terminadas.push(orden);
    } else {
      pendientes.push(orden);
    }
  }

  return { enCurso, pendientes, terminadas };
}

/** Una OT con fecha de arranque le gana a una sin fecha; entre dos, la más nueva. */
function arrancoDespues(candidata: OrdenDeImpresora, actual: OrdenDeImpresora): boolean {
  const a = candidata.fechaInicioImpresion;
  const b = actual.fechaInicioImpresion;
  if (!a) return false;
  return !b || a > b;
}
